import React from "react";
import "../Styles/style.css";
import BackHeader from "./BackHeader/BackHeader";

const TOEFLStudyGuide = () => {
  return (
    <div className="container">
      <BackHeader />
      <h1 className="display-4 text-center mb-4">TOEFL Study Guide</h1>
      <p className="lead text-center mb-5">
        Below is a comprehensive TOEFL study guide covering all four sections:
        Reading, Listening, Speaking, and Writing. This guide provides an
        overview of each section's format, detailed strategies, recommended
        practice methods, and tips to help you succeed on test day.
      </p>

      <h2 className="mb-4">Table of Contents</h2>
      <ol className="list-group mb-5">
        <li className="list-group-item">
          <a href="#introduction">
            <strong>1. Introduction to TOEFL</strong>
          </a>
        </li>
        <li className="list-group-item">
          <a href="#study-tips">
            <strong>2. General Study Tips</strong>
          </a>
        </li>
        <li className="list-group-item">
          <a href="#reading-section">
            <strong>3. Reading Section</strong>
          </a>
        </li>
        <li className="list-group-item">
          <a href="#listening-section">
            <strong>4. Listening Section</strong>
          </a>
        </li>
        <li className="list-group-item">
          <a href="#speaking-section">
            <strong>5. Speaking Section</strong>
          </a>
        </li>
        <li className="list-group-item">
          <a href="#writing-section">
            <strong>6. Writing Section</strong>
          </a>
        </li>
        <li className="list-group-item">
          <a href="#recommended-resources">
            <strong>7. Recommended Resources</strong>
          </a>
        </li>
        <li className="list-group-item">
          <a href="#test-day-tips">
            <strong>8. Test Day Tips</strong>
          </a>
        </li>
        <li className="list-group-item">
          <a href="#final-thoughts">
            <strong>9. Final Thoughts & Next Steps</strong>
          </a>
        </li>
      </ol>

      <section id="introduction" className="mb-5">
        <h3 className="mb-3">1. Introduction to TOEFL</h3>
        <div className="card">
          <div className="card-body">
            <p>
              The TOEFL (Test of English as a Foreign Language) measures the
              English language proficiency of non-native speakers. It assesses
              skills in{" "}
              <strong>Reading, Listening, Speaking, and Writing</strong>—all
              within an academic context.
            </p>
            <ul>
              <li>
                <strong>Test Length:</strong> Approximately 3 hours.
              </li>
              <li>
                <strong>Scoring:</strong> Each section is scored on a scale of
                0–30, for a total score of 0–120.
              </li>
              <li>
                <strong>Purpose:</strong> Primarily used by universities and
                colleges to evaluate applicants' English readiness.
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section id="study-tips" className="mb-5">
        <h3 className="mb-3">2. General Study Tips</h3>
        <div className="card">
          <div className="card-body">
            <ol>
              <li>
                <strong>Build Strong Foundations:</strong> Improve your general
                English proficiency by reading widely, listening to podcasts,
                and practicing conversational English daily.
              </li>
              <li>
                <strong>Create a Study Schedule:</strong> Allocate time each day
                for each TOEFL section.
              </li>
              <li>
                <strong>Use Official and High-Quality Materials:</strong>{" "}
                Practice with ETS-official resources or reputable third-party
                TOEFL prep books.
              </li>
              <li>
                <strong>Simulate Test Conditions:</strong> Use a timer and
                replicate the environment to reduce test-day anxiety.
              </li>
              <li>
                <strong>Track Your Progress:</strong> Record practice scores and
                focus your study on areas needing improvement.
              </li>
            </ol>
          </div>
        </div>
      </section>

      <section id="reading-section" className="mb-5">
        <h3 className="mb-3">3. Reading Section</h3>
        <div className="card">
          <div className="card-body">
            <h4>Format Overview</h4>
            <ul>
              <li>
                <strong>Number of Passages:</strong> 3–4
              </li>
              <li>
                <strong>Length of Each Passage:</strong> ~700 words
              </li>
              <li>
                <strong>Time Allotment:</strong> 54–72 minutes total
              </li>
              <li>
                <strong>Question Types:</strong> Factual/Detail, Inference,
                Vocabulary, Sentence Simplification, Purpose, Summary, Table
                Completion
              </li>
            </ul>
            <h4>Essential Strategies</h4>
            <ol>
              <li>
                <strong>Skim the Passage First:</strong> Identify the main idea
                and key themes.
              </li>
              <li>
                <strong>Identify Question Types:</strong> Understand the
                different question types and their requirements.
              </li>
              <li>
                <strong>Active Reading & Note-taking:</strong> Highlight
                important concepts and signal words.
              </li>
              <li>
                <strong>Look Back to Confirm:</strong> Use keywords to verify
                accuracy.
              </li>
              <li>
                <strong>Manage Time Efficiently:</strong> Aim for about 1.5
                minutes per question.
              </li>
            </ol>
            <h4>Practice Techniques</h4>
            <ul>
              <li>Read academic articles.</li>
              <li>Do practice passages under timed conditions.</li>
              <li>Focus on weaknesses.</li>
            </ul>
            <h4>Common Pitfalls</h4>
            <ul>
              <li>Spending too long on a single question.</li>
              <li>Reading every word in detail without skimming first.</li>
              <li>Misidentifying question types.</li>
            </ul>
          </div>
        </div>
      </section>

      <section id="listening-section" className="mb-5">
        <h3 className="mb-3">4. Listening Section</h3>
        <div className="card">
          <div className="card-body">
            <h4>Format Overview</h4>
            <ul>
              <li>
                <strong>Number of Audio Clips:</strong> 4–6 lectures + 2–3
                conversations
              </li>
              <li>
                <strong>Time Allotment:</strong> 41–57 minutes
              </li>
              <li>
                <strong>Audio Types:</strong> Academic lectures, campus
                conversations
              </li>
              <li>
                <strong>Question Types:</strong> Main Idea, Detail, Inference,
                Speaker Attitude, Organization
              </li>
            </ul>
            <h4>Essential Strategies</h4>
            <ol>
              <li>
                <strong>Note-taking:</strong> Develop a shorthand and focus on
                key points.
              </li>
              <li>
                <strong>Understand Structure:</strong> Recognize the structure
                of lectures and conversations.
              </li>
              <li>
                <strong>Identify Question Types Quickly:</strong> Summarize the
                overall topic and match specifics.
              </li>
              <li>
                <strong>Stay Focused:</strong> Keep your attention high, as you
                cannot replay the audio.
              </li>
            </ol>
            <h4>Practice Techniques</h4>
            <ul>
              <li>Listen to podcasts and lectures.</li>
              <li>Take notes and summarize immediately after listening.</li>
              <li>Review and self-check your summaries.</li>
            </ul>
            <h4>Common Pitfalls</h4>
            <ul>
              <li>Overly detailed notes.</li>
              <li>Relying on memory instead of effective note organization.</li>
              <li>Getting confused by minor details.</li>
            </ul>
          </div>
        </div>
      </section>

      <section id="speaking-section" className="mb-5">
        <h3 className="mb-3">5. Speaking Section</h3>
        <div className="card">
          <div className="card-body">
            <h4>Format Overview</h4>
            <ul>
              <li>
                <strong>Tasks:</strong> 4 total
              </li>
              <li>
                <strong>Time Constraints:</strong> Prep time: 15–30 seconds;
                Speaking time: 45–60 seconds
              </li>
            </ul>
            <h4>Essential Strategies</h4>
            <ol>
              <li>
                <strong>Task Familiarity:</strong> Know the differences among
                the four tasks.
              </li>
              <li>
                <strong>Structuring Responses:</strong> Use a clear
                introduction, body, and conclusion.
              </li>
              <li>
                <strong>Note-taking:</strong> Jot down essential points from
                prompts.
              </li>
              <li>
                <strong>Clarity & Fluency:</strong> Use natural speech and avoid
                excessive fillers.
              </li>
            </ol>
            <h4>Practice Techniques</h4>
            <ul>
              <li>Record yourself and evaluate clarity and fluency.</li>
              <li>Use the shadowing technique to mimic native speakers.</li>
              <li>Practice with classmates or online language partners.</li>
            </ul>
            <h4>Common Pitfalls</h4>
            <ul>
              <li>Speaking too fast or too slow.</li>
              <li>Not directly answering the prompt.</li>
              <li>Overcomplicating responses.</li>
            </ul>
          </div>
        </div>
      </section>

      <section id="writing-section" className="mb-5">
        <h3 className="mb-3">6. Writing Section</h3>
        <div className="card">
          <div className="card-body">
            <h4>Format Overview</h4>
            <ul>
              <li>
                <strong>Tasks:</strong> 2 total
              </li>
            </ul>
            <h4>Essential Strategies</h4>
            <ol>
              <li>
                <strong>Planning/Outlining:</strong> Spend 2–3 minutes outlining
                key points.
              </li>
              <li>
                <strong>Clear Essay Structure:</strong> Use a clear
                introduction, body paragraphs, and conclusion.
              </li>
              <li>
                <strong>Paraphrasing & Summarizing:</strong> Avoid directly
                copying phrases from the source.
              </li>
              <li>
                <strong>Use Strong, Varied Language:</strong> Incorporate
                appropriate transitional phrases.
              </li>
              <li>
                <strong>Proofread:</strong> Reserve 2–3 minutes at the end to
                check for errors.
              </li>
            </ol>
            <h4>Practice Techniques</h4>
            <ul>
              <li>Write under strict time limits.</li>
              <li>Get feedback from online writing communities.</li>
              <li>Focus on weaknesses in grammar and structure.</li>
            </ul>
            <h4>Common Pitfalls</h4>
            <ul>
              <li>Running out of time due to inadequate planning.</li>
              <li>Excessive quoting from the reading in integrated tasks.</li>
              <li>Poor organization leading to unclear argumentation.</li>
            </ul>
          </div>
        </div>
      </section>

      <section id="recommended-resources" className="mb-5">
        <h3 className="mb-3">7. Recommended Resources</h3>
        <div className="card">
          <div className="card-body">
            <ul>
              <li>
                <strong>ETS Official Guide to the TOEFL Test:</strong> Includes
                authentic practice questions and tests.
              </li>
              <li>
                <strong>TOEFL iBT Official Practice Tests (Online):</strong>{" "}
                Simulates the real test interface and timing.
              </li>
              <li>
                <strong>Third-Party Prep Books:</strong> Kaplan, Barron's,
                Princeton Review offer structured study plans.
              </li>
              <li>
                <strong>Online Platforms:</strong> YouTube channels and podcasts
                to boost listening skills.
              </li>
              <li>
                <strong>Apps & Websites:</strong> Quizlet for vocabulary
                building, Grammarly for writing feedback.
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section id="test-day-tips" className="mb-5">
        <h3 className="mb-3">8. Test Day Tips</h3>
        <div className="card">
          <div className="card-body">
            <ul>
              <li>
                <strong>Get Adequate Rest:</strong> Sleep well the night before.
              </li>
              <li>
                <strong>Arrive Early:</strong> Give yourself enough time for
                check-in procedures.
              </li>
              <li>
                <strong>Bring Proper ID:</strong> Check ETS requirements for
                acceptable identification.
              </li>
              <li>
                <strong>Stay Calm:</strong> If you miss a question, don't
                dwell—move on.
              </li>
              <li>
                <strong>Manage Time:</strong> Watch the on-screen timer.
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section id="final-thoughts" className="mb-5">
        <h3 className="mb-3">9. Final Thoughts & Next Steps</h3>
        <div className="card">
          <div className="card-body">
            <ul>
              <li>
                <strong>Consistency is Key:</strong> Short, daily study sessions
                are more effective.
              </li>
              <li>
                <strong>Focus on Weak Areas:</strong> Use your practice test
                results to determine which section(s) need extra attention.
              </li>
              <li>
                <strong>Develop Overall English Proficiency:</strong> Read,
                listen, speak, and write in English as often as possible.
              </li>
              <li>
                <strong>Stay Motivated:</strong> Set small milestones and reward
                yourself for progress.
              </li>
            </ul>
          </div>
        </div>
      </section>

      <p className="text-center mt-5">
        <strong>Good luck</strong> on your TOEFL journey! With clear goals,
        targeted practice, and these strategies in hand, you'll be well-prepared
        to reach the score you need.
      </p>
    </div>
  );
};

export default TOEFLStudyGuide;
